/**
 * KAN-883: график "Лиды по дням": столбцы по дням, линия отправленных (своя ось справа:
 * рассылка обычно на порядок больше).
 * KAN-920: столбец отказов. KAN-928: столбца "Передано" нет; "Лиды" считаются без отказов
 * (crmStatsChartDays); серии включаются и выключаются переключателями над графиком или
 * кликом по легенде, выбор запоминается (useCrmStatsChartSeries). Таблица под графиком
 * показывает исходные цифры.
 */
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useMemo } from 'react';
import { useCrmStatsChartSeries } from '@/hooks/useCrmStatsChartSeries';
import { crmStatsChartDays } from '@/lib/crmStats';
import { CRM_STATS_CHART_SERIES, type CrmStatsChartSeriesKey } from '@/lib/crmStatsChartSeries';
import { cn } from '@/lib/utils';
import type { CrmDailyStatsDay } from '@/types/crm';

const shortDay = (date: string) => `${Number(date.slice(8, 10))}.${date.slice(5, 7)}`;

const SERIES_BY_KEY = new Map<string, CrmStatsChartSeriesKey>(CRM_STATS_CHART_SERIES.map((s) => [s.key, s.key]));

export function CrmStatsChart({ days }: { days: readonly CrmDailyStatsDay[] }): JSX.Element {
  const data = useMemo(() => crmStatsChartDays(days), [days]);
  const { isHidden, toggle } = useCrmStatsChartSeries();

  return (
    <div data-testid="crm-stats-chart" className="rounded-lg border bg-white p-3">
      <div className="mb-2 flex flex-wrap gap-1.5" role="group" aria-label="Серии графика">
        {CRM_STATS_CHART_SERIES.map((s) => {
          const on = !isHidden(s.key);
          return (
            <button
              key={s.key}
              type="button"
              aria-pressed={on}
              title={s.name}
              onClick={() => toggle(s.key)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium transition-colors',
                on ? 'border-slate-300 bg-white text-slate-700' : 'border-slate-200 bg-slate-50 text-slate-400'
              )}
            >
              <span
                aria-hidden
                className={cn('inline-block h-2.5 w-2.5 rounded-sm', !on && 'opacity-30')}
                style={{ backgroundColor: s.color }}
              />
              {s.label}
            </button>
          );
        })}
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="date" tickFormatter={shortDay} tick={{ fontSize: 12 }} minTickGap={12} />
          <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 12 }} />
          <YAxis yAxisId="right" orientation="right" allowDecimals={false} tick={{ fontSize: 12 }} />
          <Tooltip labelFormatter={(label) => shortDay(String(label))} />
          <Legend
            wrapperStyle={{ fontSize: 12, cursor: 'pointer' }}
            inactiveColor="#cbd5e1"
            onClick={(entry) => {
              const key = SERIES_BY_KEY.get(String(entry.dataKey));
              if (key) toggle(key);
            }}
          />
          {CRM_STATS_CHART_SERIES.map((s) =>
            s.kind === 'bar' ? (
              <Bar
                key={s.key}
                yAxisId={s.axis}
                dataKey={s.key}
                name={s.name}
                fill={s.color}
                radius={[3, 3, 0, 0]}
                hide={isHidden(s.key)}
              />
            ) : (
              <Line
                key={s.key}
                yAxisId={s.axis}
                type="monotone"
                dataKey={s.key}
                name={s.name}
                stroke={s.color}
                strokeWidth={2}
                dot={false}
                hide={isHidden(s.key)}
              />
            )
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
